/**
 * Wishlist state for the signed-in dealer.
 *
 * COST SHAPE — the reason this hook looks the way it does:
 *  - The saved list lives on the dealer's own `users/{uid}` doc, which
 *    AuthContext already streams. So reading the wishlist costs NOTHING extra,
 *    and it stays in sync across the dealer's devices for free.
 *  - Taps are applied to a local overlay immediately (so the heart never lags)
 *    and the resulting list is written ONCE per burst, debounced. Tapping a
 *    heart ten times in a row is one write, not ten.
 *  - Nothing writes to a document shared between dealers, so there is no
 *    per-document write-rate hotspot anywhere in this feature.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Product } from '@mitang/shared';
import { WISHLIST_MAX } from '@mitang/shared';
import { useAuth } from '../features/auth/AuthContext';
import { useToast } from '../components/Toast';
import { writeWishlist } from '../lib/repo';
import { logActivity } from '../lib/activity/activityLogger';

/** How long to wait after the last tap before writing. */
const FLUSH_DELAY_MS = 600;

export interface WishlistApi {
  /** Saved product ids, newest first (server list with local taps applied). */
  ids: string[];
  isSaved: (productId: string) => boolean;
  /** Toggle a product. Safe to call rapidly — writes are coalesced. */
  toggle: (product: Product) => void;
  count: number;
}

export function useWishlist(): WishlistApi {
  const { profile } = useAuth();
  const toast = useToast();

  /** productId → desired state, for taps the server hasn't confirmed yet. */
  const [overlay, setOverlay] = useState<Record<string, boolean>>({});
  const pending = useRef<Record<string, boolean>>({});
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Keep the newest profile in a ref so a queued flush always computes from the
  // latest server list, not the one captured when the tap happened.
  const profileRef = useRef(profile);
  profileRef.current = profile;

  const serverIds = useMemo(() => profile?.wishlist ?? [], [profile?.wishlist]);

  // Drop overlay entries the server now agrees with, so the two can't drift.
  useEffect(() => {
    setOverlay((current) => {
      const remaining: Record<string, boolean> = {};
      for (const [id, wanted] of Object.entries(current)) {
        if (serverIds.includes(id) !== wanted) remaining[id] = wanted;
      }
      return Object.keys(remaining).length === Object.keys(current).length ? current : remaining;
    });
  }, [serverIds]);

  const ids = useMemo(() => {
    const added = Object.entries(overlay)
      .filter(([, wanted]) => wanted)
      .map(([id]) => id);
    return [...added, ...serverIds].filter(
      (id, i, all) => all.indexOf(id) === i && overlay[id] !== false,
    );
  }, [overlay, serverIds]);

  const isSaved = useCallback((productId: string) => ids.includes(productId), [ids]);

  const flush = useCallback(async () => {
    timer.current = null;
    const user = profileRef.current;
    const changes = pending.current;
    pending.current = {};
    if (!user || Object.keys(changes).length === 0) return;

    let next = user.wishlist ?? [];
    for (const [id, wanted] of Object.entries(changes)) {
      next = wanted ? [id, ...next.filter((x) => x !== id)] : next.filter((x) => x !== id);
    }
    try {
      await writeWishlist(user, next);
    } catch {
      // The write failed (offline, rules). Clear the overlay so the UI falls
      // back to the server truth rather than showing a save that never happened.
      setOverlay({});
      toast.show({ title: 'Not saved', body: 'Could not reach the server. Please try again.' });
    }
  }, [toast]);

  const toggle = useCallback(
    (product: Product) => {
      const user = profileRef.current;
      if (!user) return;
      const wanted = !isSaved(product.id);

      if (wanted && ids.length >= WISHLIST_MAX) {
        toast.show({
          title: 'Wishlist full',
          body: `You can save up to ${WISHLIST_MAX} products. Remove one first.`,
        });
        return;
      }

      setOverlay((o) => ({ ...o, [product.id]: wanted }));
      pending.current[product.id] = wanted;

      // Logged for the admin's activity feed / CSV, with the model number so the
      // report reads like a product name and not an internal id.
      logActivity(wanted ? 'wishlisted' : 'unwishlisted', {
        productId: product.id,
        modelNumber: product.modelNumber,
        category: product.category,
      });

      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(() => void flush(), FLUSH_DELAY_MS);
    },
    [flush, ids.length, isSaved, toast],
  );

  // Don't lose a queued tap if the screen unmounts first.
  useEffect(
    () => () => {
      if (timer.current) {
        clearTimeout(timer.current);
        void flush();
      }
    },
    [flush],
  );

  return { ids, isSaved, toggle, count: ids.length };
}
